// Canonical campaign identity resolver — the single place where campaign
// identity is merged and displayed across the app (and mirrored byte-for-byte
// in the superadmin app).
//
// Inputs (all optional):
//   - liveCampaigns:               rows from /api/meta/campaigns
//   - importedReportingCampaigns:  rows from /api/media-campaigns/imported-reporting-context
//   - mediaCampaigns:              rows from /api/media-campaigns (enrichment only
//                                  by default — see governedCampaignOptionFilter)
//   - reportingContext:            raw imported-reporting-context payload (convenience)
//   - governedCampaignOptionFilter: OPT-IN predicate. When supplied, governed
//                                  media campaigns that satisfy it also become
//                                  visible options instead of enriching only.
//
// Output: one canonical campaign object per real-world campaign.
//
// Rules:
//   - One campaign = one visible option (identity ids + normalized name keys dedupe).
//   - The live Meta name always wins for display; internal names never display
//     when a Meta identity exists.
//   - Reporting rows enrich the canonical object (metrics, reporting ids,
//     read-only state) instead of creating a second option.
//   - Governed media campaigns enrich by default. A caller that governs its own
//     eligibility (Campaign Optimizer) may opt in via governedCampaignOptionFilter
//     so an approved campaign with no ad-platform identity is still selectable —
//     it merges into a matching live/reporting option rather than duplicating it.
//   - Live ids, reporting ids, external ids, metrics, and read-only state are
//     all preserved on the merged object.
//
// No page should merge campaign lists independently — consume
// resolveCanonicalCampaigns (or the legacy buildOptimizerCampaignOptions
// shim, which delegates here).

import { getCanonicalCampaignDisplayName } from "./campaignDisplayName";

export const IMPORTED_REPORTING_CONTEXT_PATH =
  "/api/media-campaigns/imported-reporting-context";

const normalizeText = (value) => String(value || "").trim();

const normalizeKey = (value) =>
  normalizeText(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");

const CAMPAIGN_NAME_DECORATOR_PATTERN = /\s*\((?:reporting[_\s-]*only|imported|paused)\)\s*/gi;

const cleanCampaignDisplayName = (value) =>
  normalizeText(value)
    .replace(CAMPAIGN_NAME_DECORATOR_PATTERN, " ")
    .replace(/\s+/g, " ")
    .trim();

// Exported: the Platform Analytics campaign-scope handoff resolves a carried
// campaign name against a destination catalogue and must key names EXACTLY as
// the merge above does, or the two would disagree about what one campaign is.
export const normalizeCampaignNameKey = (value) =>
  normalizeKey(cleanCampaignDisplayName(value));

const normalizeCampaignIdKey = (value) =>
  normalizeKey(normalizeText(value).replace(/^reporting:/i, ""));

const collectCampaignNameKeys = (...values) =>
  Array.from(
    new Set(values.map(normalizeCampaignNameKey).filter(Boolean))
  );

const toFiniteMetricNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const normalized =
    typeof value === "string" ? value.replace(/[$,%\s,]/g, "") : value;
  const number = Number(normalized);
  return Number.isFinite(number) ? number : null;
};

const hasUsableMetricFields = (source = {}) => {
  const metricSources = [source, source.metrics, source.kpis, source.currentKpis].filter(
    (metrics) => metrics && typeof metrics === "object"
  );
  return metricSources.some((metrics) =>
    [
      metrics.spend,
      metrics.totalSpend,
      metrics.spendToDate,
      metrics.impressions,
      metrics.clicks,
    ].some((value) => {
      const number = toFiniteMetricNumber(value);
      return number !== null && number > 0;
    })
  );
};

const parsePayload = (value) => {
  if (!value) return null;
  if (typeof value === "object" && !Array.isArray(value)) return value;
  if (typeof value !== "string") return null;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

export const extractImportedReportingCampaigns = (contextPayload) =>
  Array.isArray(contextPayload?.importedReportingCampaigns)
    ? contextPayload.importedReportingCampaigns
    : [];

// Reporting/optimizer selector exclusion — mirrors the backend
// mediaAuthorizationVisibilityService.isReportingExcludedCampaign. Only the
// supervised-flight PROOF object (born-paused, e.g. cie_…supervisedflight
// [ck:…]) and the internal "authorization evidence" MediaCampaign row are
// excluded from selectors in Insights / Analysis / Optimizer.
//
// DELIBERATELY NARROW: it must NOT include broad internal-tooling markers like
// ads_read / verification, because the advertiser's legitimate imported
// reporting bridge (e.g. "Cphere AI Meta ads_read Test Campaign", which
// carries the real recorded Meta App Review delivery) is a genuine reporting
// campaign that must stay selectable. Plain REPORTING_ONLY / read-only /
// created-for-review is NOT a reason to exclude.
const REPORTING_EXCLUDED_NAME_MARKERS = [
  /supervised[\s_-]*(?:draft|setup)?[\s_-]*flight/i,
  /\bsupervisedflight\b/i,
  /\bdwg8\b/i,
  /draft[\s_-]*flight[\s_-]*authorization/i,
];

export const isInternalReportingExcludedCampaign = (rowInput) => {
  const row = rowInput && typeof rowInput === "object" ? rowInput : null;
  if (!row) return false;
  const payload = parsePayload(row.strategy_payload || row.strategyPayload) || {};
  if (payload.internalOnly === true) return true;
  const name = normalizeText(
    row.campaign_name ||
      row.campaignName ||
      row.name ||
      row.meta_campaign_name ||
      row.metaCampaignName
  );
  return REPORTING_EXCLUDED_NAME_MARKERS.some((re) => re.test(name));
};

const getReportingContext = (campaign = {}) => {
  const payload = parsePayload(campaign.strategy_payload || campaign.strategyPayload);
  const external =
    payload?.externalReporting && typeof payload.externalReporting === "object"
      ? payload.externalReporting
      : {};
  return {
    readOnly: external.readOnly === true || payload?.reportingOnly === true,
    externalCampaignId: normalizeText(external.externalCampaignId || external.external_campaign_id),
    externalCampaignName: normalizeText(
      external.externalCampaignName || external.external_campaign_name
    ),
  };
};

const collectCampaignIdentityIds = (row = {}, reporting = {}, { includeRowId = false } = {}) => {
  const ids = [
    reporting.externalCampaignId,
    row.externalCampaignId,
    row.external_campaign_id,
    row.metaCampaignId,
    row.meta_campaign_id,
    row.campaignId,
    row.campaign_id,
    includeRowId ? row.id : null,
  ]
    .map(normalizeCampaignIdKey)
    .filter(Boolean);
  return Array.from(new Set(ids));
};

const toPublicOption = ({
  identityIds: _identityIds,
  nameKey: _nameKey,
  nameKeys: _nameKeys,
  hasExternalCampaignIdentity: _hasExternalCampaignIdentity,
  ...option
}) => option;

const firstCampaignName = (...values) => {
  for (const value of values) {
    const cleaned = cleanCampaignDisplayName(value);
    if (cleaned) return cleaned;
  }
  return "";
};

const pickMetricFields = (row = {}) => ({
  spend: row.spend ?? row.totalSpend ?? row.spendToDate,
  totalSpend: row.totalSpend ?? row.spend ?? row.spendToDate,
  spendToDate: row.spendToDate,
  impressions: row.impressions,
  clicks: row.clicks,
  metrics: row.metrics,
  kpis: row.kpis,
  currentKpis: row.currentKpis,
});

const normalizeLiveCampaignOption = (row = {}) => {
  const id = normalizeText(row.id || row.campaign_id || row.campaignId);
  const nameCandidates = [
    row.metaCampaignName,
    row.meta_campaign_name,
    row.liveMetaCampaignName,
    row.live_meta_campaign_name,
    row.externalCampaignName,
    row.external_campaign_name,
    row.linkedExternalCampaignName,
    row.linked_external_campaign_name,
    row.displayCampaignName,
    row.display_campaign_name,
    row.canonicalCampaignName,
    row.canonical_campaign_name,
    row.campaign?.name,
    row.campaign?.campaign_name,
    row.name,
    row.campaign_name,
    row.campaignName,
    id,
  ];
  const name = firstCampaignName(...nameCandidates);
  if (!id || !name) return null;
  return {
    id,
    name,
    displayName: name,
    ...pickMetricFields(row),
    liveMetrics: pickMetricFields(row),
    status: normalizeText(row.effective_status || row.status || "UNKNOWN") || "UNKNOWN",
    objective: row.objective || null,
    reportingOnly: false,
    liveCampaignId: id,
    externalCampaignId: normalizeText(
      row.externalCampaignId ||
        row.external_campaign_id ||
        row.metaCampaignId ||
        row.meta_campaign_id ||
        row.campaignId ||
        row.campaign_id ||
        id
    ),
    hasReportingContext: false,
    identityIds: collectCampaignIdentityIds(row, {}, { includeRowId: true }),
    nameKey: normalizeCampaignNameKey(name),
    nameKeys: collectCampaignNameKeys(...nameCandidates, name),
  };
};

const normalizeImportedCampaignOption = (row = {}) => {
  const reporting = getReportingContext(row);
  const externalCampaignId = normalizeText(
    reporting.externalCampaignId ||
      row.externalCampaignId ||
      row.external_campaign_id ||
      row.metaCampaignId ||
      row.meta_campaign_id
  );
  const reportingOptionId = `reporting:${normalizeText(
    externalCampaignId ||
      row.campaignId ||
      row.campaign_id ||
      row.id ||
      row.externalCampaignId ||
      row.external_campaign_id ||
      row.metaCampaignId ||
      row.meta_campaign_id ||
      row.campaign_name ||
      row.campaignName ||
      row.name
  )}`;
  const nameCandidates = [
    reporting.externalCampaignName,
    row.externalCampaignName,
    row.external_campaign_name,
    row.metaCampaignName,
    row.meta_campaign_name,
    row.liveMetaCampaignName,
    row.live_meta_campaign_name,
    row.linkedExternalCampaignName,
    row.linked_external_campaign_name,
    row.displayCampaignName,
    row.display_campaign_name,
    row.canonicalCampaignName,
    row.canonical_campaign_name,
    row.campaign?.name,
    row.campaign?.campaign_name,
    row.name,
    row.campaign_name,
    row.campaignName,
  ];
  const name = firstCampaignName(...nameCandidates);
  if (!name) return null;
  return {
    id: reportingOptionId,
    name,
    displayName: name,
    ...pickMetricFields(row),
    reportingMetrics: pickMetricFields(row),
    status: normalizeText(row.effective_status || row.status || "REPORTING_ONLY") || "REPORTING_ONLY",
    objective: row.objective || null,
    reportingOnly: true,
    readOnly: reporting.readOnly,
    reportingCampaignId: normalizeText(row.id || row.campaignId || row.campaign_id),
    reportingOptionId,
    externalCampaignId,
    externalCampaignName: name,
    hasExternalCampaignIdentity: Boolean(externalCampaignId),
    hasReportingContext: true,
    identityIds: collectCampaignIdentityIds(row, reporting),
    nameKey: normalizeCampaignNameKey(name),
    nameKeys: collectCampaignNameKeys(...nameCandidates, name),
  };
};

/*
 * Governed media campaign → canonical option.
 *
 * A governed campaign is the advertiser's own approved plan row. It is a real
 * campaign whether or not an ad platform ever minted an id for it, so the
 * option's identity is its stable media campaign id — never its name, and never
 * an ad-platform id it may not have. Callers opt in through
 * governedCampaignOptionFilter; the eligibility rule itself lives with the
 * caller so this resolver never grows a second definition of "approved".
 */
const normalizeGovernedCampaignOption = (row = {}) => {
  const mediaCampaignId = normalizeText(row.id || row.mediaCampaignId || row.media_campaign_id);
  const name = firstCampaignName(getCanonicalCampaignDisplayName(row));
  if (!mediaCampaignId || !name) return null;

  const reporting = getReportingContext(row);
  const nameCandidates = [
    name,
    reporting.externalCampaignName,
    row.campaign_name,
    row.campaignName,
    row.name,
  ];

  return {
    id: mediaCampaignId,
    name,
    displayName: name,
    ...pickMetricFields(row),
    status: normalizeText(row.status || row.contract_status || row.contractStatus) || "APPROVED",
    objective: row.objective || null,
    reportingOnly: false,
    governed: true,
    mediaCampaignId,
    flightStartDate: row.flight_start_date || row.flightStartDate || null,
    flightEndDate: row.flight_end_date || row.flightEndDate || null,
    externalCampaignId: reporting.externalCampaignId || "",
    readOnly: reporting.readOnly === true,
    hasReportingContext: false,
    identityIds: collectCampaignIdentityIds(row, reporting, { includeRowId: true }),
    nameKey: normalizeCampaignNameKey(name),
    nameKeys: collectCampaignNameKeys(...nameCandidates),
  };
};

const mergeIdentityIds = (...sources) =>
  Array.from(
    new Set(
      sources
        .flatMap((source) => (Array.isArray(source?.identityIds) ? source.identityIds : []))
        .filter(Boolean)
    )
  );

const mergeNameKeys = (...sources) =>
  Array.from(
    new Set(
      sources
        .flatMap((source) => {
          const keys = Array.isArray(source?.nameKeys) ? source.nameKeys : [];
          return [...keys, source?.nameKey];
        })
        .filter(Boolean)
    )
  );

const mergeCampaignOptions = (existing, incoming) => {
  const identityIds = mergeIdentityIds(existing, incoming);
  const nameKeys = mergeNameKeys(existing, incoming);
  const nameKey =
    existing.nameKey ||
    incoming.nameKey ||
    nameKeys[0] ||
    normalizeCampaignNameKey(existing.name || incoming.name) ||
    normalizeCampaignIdKey(existing.id || incoming.id);
  const live = !incoming.reportingOnly ? incoming : !existing.reportingOnly ? existing : null;
  const reporting = incoming.reportingOnly ? incoming : existing.reportingOnly ? existing : null;

  if (live && reporting) {
    const liveHasUsableMetrics = hasUsableMetricFields(live.liveMetrics || live);
    const primaryMetrics = pickMetricFields(liveHasUsableMetrics ? live : reporting);
    const secondaryMetrics = pickMetricFields(liveHasUsableMetrics ? reporting : live);
    const mergedName = live.name || reporting.name;
    return {
      ...reporting,
      ...live,
      id: live.id,
      name: mergedName,
      displayName: mergedName,
      spend: primaryMetrics.spend ?? secondaryMetrics.spend,
      totalSpend: primaryMetrics.totalSpend ?? secondaryMetrics.totalSpend,
      spendToDate: primaryMetrics.spendToDate ?? secondaryMetrics.spendToDate,
      impressions: primaryMetrics.impressions ?? secondaryMetrics.impressions,
      clicks: primaryMetrics.clicks ?? secondaryMetrics.clicks,
      metrics: primaryMetrics.metrics ?? secondaryMetrics.metrics,
      kpis: primaryMetrics.kpis ?? secondaryMetrics.kpis,
      currentKpis: primaryMetrics.currentKpis ?? secondaryMetrics.currentKpis,
      liveMetrics: live.liveMetrics || pickMetricFields(live),
      reportingMetrics: reporting.reportingMetrics || pickMetricFields(reporting),
      status: live.status || reporting.status,
      objective: live.objective ?? reporting.objective ?? null,
      reportingOnly: false,
      liveCampaignId: live.liveCampaignId || live.id,
      reportingCampaignId: reporting.reportingCampaignId || reporting.id,
      reportingOptionId: reporting.reportingOptionId || reporting.id,
      externalCampaignId: live.externalCampaignId || reporting.externalCampaignId || "",
      externalCampaignName: reporting.externalCampaignName || reporting.name || live.name || "",
      readOnly: reporting.readOnly === true || live.readOnly === true,
      hasExternalCampaignIdentity:
        live.hasExternalCampaignIdentity === true ||
        reporting.hasExternalCampaignIdentity === true,
      hasReportingContext: true,
      governed: existing.governed === true || incoming.governed === true,
      mediaCampaignId: existing.mediaCampaignId || incoming.mediaCampaignId || null,
      flightStartDate: existing.flightStartDate || incoming.flightStartDate || null,
      flightEndDate: existing.flightEndDate || incoming.flightEndDate || null,
      identityIds,
      nameKey,
      nameKeys,
    };
  }

  const mergedName = existing.name || incoming.name;
  return {
    ...existing,
    name: mergedName,
    displayName: mergedName,
    spend: existing.spend ?? incoming.spend,
    totalSpend: existing.totalSpend ?? incoming.totalSpend,
    spendToDate: existing.spendToDate ?? incoming.spendToDate,
    impressions: existing.impressions ?? incoming.impressions,
    clicks: existing.clicks ?? incoming.clicks,
    metrics: existing.metrics ?? incoming.metrics,
    kpis: existing.kpis ?? incoming.kpis,
    currentKpis: existing.currentKpis ?? incoming.currentKpis,
    liveMetrics: existing.liveMetrics ?? incoming.liveMetrics,
    reportingMetrics: existing.reportingMetrics ?? incoming.reportingMetrics,
    objective: existing.objective ?? incoming.objective ?? null,
    liveCampaignId: existing.liveCampaignId || incoming.liveCampaignId || existing.id,
    reportingCampaignId: existing.reportingCampaignId || incoming.reportingCampaignId,
    reportingOptionId: existing.reportingOptionId || incoming.reportingOptionId,
    externalCampaignId: existing.externalCampaignId || incoming.externalCampaignId || "",
    externalCampaignName: existing.externalCampaignName || incoming.externalCampaignName || "",
    readOnly: existing.readOnly === true || incoming.readOnly === true,
    hasReportingContext:
      existing.hasReportingContext === true || incoming.hasReportingContext === true,
    governed: existing.governed === true || incoming.governed === true,
    mediaCampaignId: existing.mediaCampaignId || incoming.mediaCampaignId || null,
    flightStartDate: existing.flightStartDate || incoming.flightStartDate || null,
    flightEndDate: existing.flightEndDate || incoming.flightEndDate || null,
    identityIds,
    nameKey,
    nameKeys,
  };
};

// Media campaigns enrich matching canonical options (media ids, flight dates,
// canonical name candidates) but never create additional visible options —
// the dropdown inventory is defined by live + imported reporting identities.
const enrichOptionsWithMediaCampaigns = (options, mediaCampaigns = []) => {
  const rows = (Array.isArray(mediaCampaigns) ? mediaCampaigns : []).filter(
    (row) => !isInternalReportingExcludedCampaign(row)
  );
  if (!rows.length) return options;

  rows.forEach((row) => {
    const reporting = getReportingContext(row);
    const identityIds = collectCampaignIdentityIds(row, reporting);
    const canonicalName = getCanonicalCampaignDisplayName(row);
    const nameKeys = collectCampaignNameKeys(
      canonicalName,
      reporting.externalCampaignName,
      row.campaign_name,
      row.campaignName,
      row.name
    );

    const match = options.find((option) => {
      const optionIdentityIds = Array.isArray(option.identityIds) ? option.identityIds : [];
      const optionNameKeys = Array.from(
        new Set([...(option.nameKeys || []), option.nameKey].filter(Boolean))
      );
      return (
        identityIds.some((id) => optionIdentityIds.includes(id)) ||
        nameKeys.some((key) => optionNameKeys.includes(key))
      );
    });
    if (!match) return;

    match.mediaCampaignId = match.mediaCampaignId || normalizeText(row.id);
    match.flightStartDate =
      match.flightStartDate || row.flight_start_date || row.flightStartDate || null;
    match.flightEndDate =
      match.flightEndDate || row.flight_end_date || row.flightEndDate || null;
    match.readOnly = match.readOnly === true || reporting.readOnly === true;
    match.identityIds = Array.from(new Set([...(match.identityIds || []), ...identityIds]));
    match.nameKeys = Array.from(new Set([...(match.nameKeys || []), ...nameKeys]));
  });

  return options;
};

export const resolveCanonicalCampaigns = ({
  liveCampaigns = [],
  importedReportingCampaigns = [],
  mediaCampaigns = [],
  reportingContext = null,
  governedCampaignOptionFilter = null,
} = {}) => {
  const options = [];

  const addOption = (option) => {
    if (!option) return;
    const identityIds = Array.isArray(option.identityIds) ? option.identityIds : [];
    const nameKey =
      option.nameKey || normalizeCampaignNameKey(option.name) || normalizeCampaignIdKey(option.id);
    const nameKeys = Array.from(
      new Set([...(Array.isArray(option.nameKeys) ? option.nameKeys : []), nameKey].filter(Boolean))
    );
    const existingIndex = options.findIndex((candidate) => {
      const candidateIdentityIds = Array.isArray(candidate.identityIds)
        ? candidate.identityIds
        : [];
      const candidateNameKeys = Array.from(
        new Set(
          [
            ...(Array.isArray(candidate.nameKeys) ? candidate.nameKeys : []),
            candidate.nameKey,
          ].filter(Boolean)
        )
      );
      const hasSharedIdentity = identityIds.some((id) => candidateIdentityIds.includes(id));
      if (hasSharedIdentity) return true;
      /*
       * The name is a dedupe fallback for rows from DIFFERENT authorities — a
       * live Meta row and the governed campaign it belongs to are one campaign
       * called one thing. Two GOVERNED rows are not: they carry distinct
       * immutable ids issued by the same authority, so they are two campaigns
       * however alike they are named, and merging them on a shared name would
       * silently delete one from the selector.
       */
      if (
        option.governed === true &&
        candidate.governed === true &&
        normalizeText(option.mediaCampaignId) !== normalizeText(candidate.mediaCampaignId)
      ) {
        return false;
      }
      return nameKeys.some((key) => candidateNameKeys.includes(key));
    });

    if (existingIndex >= 0) {
      const merged = mergeCampaignOptions(options[existingIndex], option);
      options[existingIndex] = merged;
      return;
    }

    options.push(option);
  };

  // Drop internal supervised-flight authorization/proof artifacts from every
  // input list before merge, so they can never become a visible option, the
  // auto-selected default, or a dedupe anchor that hijacks a real campaign.
  const importedRows = [
    ...(Array.isArray(importedReportingCampaigns) ? importedReportingCampaigns : []),
    ...extractImportedReportingCampaigns(reportingContext),
  ].filter((row) => !isInternalReportingExcludedCampaign(row));

  const liveOptions = (Array.isArray(liveCampaigns) ? liveCampaigns : [])
    .filter((row) => !isInternalReportingExcludedCampaign(row))
    .map(normalizeLiveCampaignOption)
    .filter(Boolean);
  const liveNameKeys = new Set(
    liveOptions.flatMap((option) => option.nameKeys || option.nameKey).filter(Boolean)
  );
  const hasLiveOptions = liveOptions.length > 0;

  liveOptions.forEach(addOption);

  importedRows
    .map(normalizeImportedCampaignOption)
    .filter((option) => {
      if (!option) return false;
      if (!hasLiveOptions) return true;
      if (option.hasExternalCampaignIdentity) return true;
      const optionNameKeys = Array.isArray(option.nameKeys) ? option.nameKeys : [option.nameKey];
      return optionNameKeys.some((key) => liveNameKeys.has(key));
    })
    .forEach(addOption);

  /*
   * Governed options are added last so a live ad-platform identity still wins
   * the display name on merge. They are added BEFORE enrichment so a governed
   * campaign with no platform identity (an approved plan the platforms never
   * minted an id for) is a first-class option rather than an invisible row.
   */
  if (typeof governedCampaignOptionFilter === "function") {
    (Array.isArray(mediaCampaigns) ? mediaCampaigns : [])
      .filter((row) => !isInternalReportingExcludedCampaign(row))
      .filter((row) => {
        try {
          return governedCampaignOptionFilter(row) === true;
        } catch {
          return false;
        }
      })
      .map(normalizeGovernedCampaignOption)
      .filter(Boolean)
      .forEach(addOption);
  }

  enrichOptionsWithMediaCampaigns(options, mediaCampaigns);

  return options
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
    .map(toPublicOption);
};

// The "Auto" dropdown entry must never render a bare campaign name — that
// reads as a duplicate of the real option (the exact defect observed in both
// apps). Always prefix so auto-selection is visually distinct.
export const resolveAutoCampaignOptionLabel = ({
  autoDetectedCampaignName = "",
  hasCampaigns = false,
} = {}) => {
  const name = cleanCampaignDisplayName(autoDetectedCampaignName);
  if (name) return `Auto — ${name}`;
  if (hasCampaigns) return "Auto (Most Active)";
  return "Auto (No Meta campaigns)";
};

// Default ("Auto") campaign selection. The canonical advertiser campaign must
// win the default even when broader visibility (superadmin) surfaces stale
// legacy live rows that sort first alphabetically — stale rows stay selectable
// manually, they just never win "Auto". Priority tiers:
//   0. merged canonical option (live identity + reporting context)
//   1. reporting-backed option (canonical reporting fallback metrics)
//   2. option linked to a current advertiser media campaign
//   3. live option that is ACTIVE or has usable delivery metrics
//   4. everything else (stale/legacy live rows)
// Within a tier: ACTIVE first, then usable metrics, then existing list order —
// which preserves the previous ACTIVE → delivery → first-option behavior for
// lists with no canonical context.
const ACTIVE_STATUS_PATTERN = /active/i;

const defaultSelectionTier = (option = {}) => {
  const isReportingBacked = option.hasReportingContext === true;
  if (isReportingBacked && option.reportingOnly !== true && option.liveCampaignId) return 0;
  if (isReportingBacked || option.reportingOnly === true) return 1;
  if (option.mediaCampaignId || option.reportingCampaignId) return 2;
  if (
    ACTIVE_STATUS_PATTERN.test(String(option.status || "")) ||
    hasUsableMetricFields(option)
  ) {
    return 3;
  }
  return 4;
};

export const resolveDefaultCampaignAnalysisCampaign = (options = []) => {
  const list = (Array.isArray(options) ? options : []).filter(
    (option) => option && typeof option === "object"
  );
  if (!list.length) return null;

  let best = null;
  let bestRank = Infinity;
  list.forEach((option) => {
    const tier = defaultSelectionTier(option);
    const activeBonus = ACTIVE_STATUS_PATTERN.test(String(option.status || "")) ? 0 : 1;
    const metricsBonus = hasUsableMetricFields(option) ? 0 : 1;
    // Strict < keeps the first option in list order on ties.
    const rank = tier * 4 + activeBonus * 2 + metricsBonus;
    if (rank < bestRank) {
      bestRank = rank;
      best = option;
    }
  });
  return best;
};

// Legacy name retained for existing consumers (Optimus, Sherloc Reports).
export const buildOptimizerCampaignOptions = resolveCanonicalCampaigns;

export { getCanonicalCampaignDisplayName };
